import type { PublicCatalogFilterOptions, PublicCatalogParamName } from './api';

export const catalogSortOptions = [
  { value: 'recent', label: 'Más recientes' },
  { value: 'price_asc', label: 'Menor precio' },
  { value: 'price_desc', label: 'Mayor precio' },
] as const;

export const catalogPageSizes = ['12', '30', '60'] as const;

/** Filters that admit several values (OR inside, AND between), in the order of the filter panel. */
export const catalogMultiFilters = [
  'brand',
  'model',
  'storage',
  'color',
] as const satisfies readonly PublicCatalogParamName[];

export type CatalogMultiFilter = (typeof catalogMultiFilters)[number];

/** Filters that narrow the result; sort, page and page size only reorder or slice it. */
export const catalogFilterParams = [
  ...catalogMultiFilters,
  'minPrice',
  'maxPrice',
] as const satisfies readonly PublicCatalogParamName[];

/** A copy of the URL query; any change to what is listed starts again from the first page. */
function withoutPage(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('page');
  return next;
}

/** Adds the value to the filter, or removes it if it was already chosen. */
export function toggleFilterValue(
  params: URLSearchParams,
  name: CatalogMultiFilter,
  value: string,
  options: PublicCatalogFilterOptions | null,
): URLSearchParams {
  const next = withoutPage(params);
  const current = next.getAll(name);
  if (!current.includes(value)) {
    next.append(name, value);
    return next;
  }
  next.delete(name);
  for (const kept of current) if (kept !== value) next.append(name, kept);
  // A model belongs to one brand: dropping the brand drops its models too.
  if (name === 'brand' && options) {
    const orphaned = new Set(
      options.models.filter((model) => model.brandId === value).map((model) => model.id),
    );
    const models = next.getAll('model');
    next.delete('model');
    for (const model of models) if (!orphaned.has(model)) next.append('model', model);
  }
  return next;
}

export function setPriceRange(
  params: URLSearchParams,
  minPrice: string | null,
  maxPrice: string | null,
): URLSearchParams {
  const next = withoutPage(params);
  if (minPrice) next.set('minPrice', minPrice);
  else next.delete('minPrice');
  if (maxPrice) next.set('maxPrice', maxPrice);
  else next.delete('maxPrice');
  return next;
}

export function clearCatalogFilters(params: URLSearchParams): URLSearchParams {
  const next = withoutPage(params);
  for (const name of catalogFilterParams) next.delete(name);
  return next;
}

/** The values of a filter in the URL, once each: a repeated value is still one filter. */
export function chosenValues(params: URLSearchParams, name: CatalogMultiFilter): string[] {
  return [...new Set(params.getAll(name))];
}

/**
 * A model is always named with its brand: two brands may share a model name, and the buyer must
 * know which one is chosen.
 */
export function modelLabel(model: { name: string; brandName: string }): string {
  return `${model.brandName} ${model.name}`;
}

export type AppliedFilter = {
  key: string;
  label: string;
  remove: (params: URLSearchParams) => URLSearchParams;
};

function formatUsd(value: string): string {
  return value.replace('.', ',');
}

/**
 * One removable tag per chosen value, in the order of the filter panel. A value with no devices
 * left (an old shared link) has no name to show, so it reads as unavailable but can be removed.
 */
export function appliedFilters(
  params: URLSearchParams,
  options: PublicCatalogFilterOptions | null,
): AppliedFilter[] {
  const names: Record<CatalogMultiFilter, (value: string) => string | undefined> = {
    brand: (value) => options?.brands.find((brand) => brand.id === value)?.name,
    model: (value) => {
      const model = options?.models.find((candidate) => candidate.id === value);
      return model ? modelLabel(model) : undefined;
    },
    storage: (value) => `${value} GB`,
    color: (value) => options?.colors.find((color) => color.id === value)?.name,
  };
  const tags: AppliedFilter[] = catalogMultiFilters.flatMap((name) =>
    chosenValues(params, name).map((value) => ({
      key: `${name}:${value}`,
      label: names[name](value) ?? 'No disponible',
      remove: (current: URLSearchParams) => toggleFilterValue(current, name, value, options),
    })),
  );

  const min = params.get('minPrice');
  const max = params.get('maxPrice');
  if (min || max) {
    tags.push({
      key: 'price',
      label:
        min && max
          ? `USD ${formatUsd(min)} – ${formatUsd(max)}`
          : min
            ? `Desde USD ${formatUsd(min)}`
            : `Hasta USD ${formatUsd(max ?? '')}`,
      remove: (current) => setPriceRange(current, null, null),
    });
  }
  return tags;
}

/**
 * Keeps only a number while the buyer types or pastes. Only a final separator followed by up to
 * two digits is a decimal one; any other is a thousands mark, as in the amounts the catalogue
 * shows ("USD 1.250,00" pastes as 1250,00, never as 1,25).
 */
export function toNumericAmount(value: string): string {
  const clean = value.replace(/[^\d.,]/g, '');
  const decimal = /^(.*)([.,])(\d{0,2})$/.exec(clean);
  const whole = (decimal ? decimal[1] : clean).replace(/[.,]/g, '').slice(0, 8);
  return decimal ? `${whole}${decimal[2]}${decimal[3]}` : whole;
}
